import { useState } from 'react';
import { X } from 'lucide-react';
import { Sprint, Task } from '../types';
import { toast } from 'sonner';
import { generateId } from '../utils/idGenerator';
import { normalizeOrganizationId } from '../utils/urlToOrg';

interface CreateSprintModalProps {
  projectId: string;
  organizationId: string;
  onClose: () => void;
  onCreate: (sprint: Sprint) => Promise<void> | void;
  existingTasks: Task[];
}

export default function CreateSprintModal({
  projectId,
  organizationId,
  onClose,
  onCreate,
  existingTasks,
}: CreateSprintModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    startDate: '',
    endDate: '',
    goals: '',
  });
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const normalizedOrganizationId = normalizeOrganizationId(organizationId);

    if (!normalizedOrganizationId) {
      toast.error('No organization selected. Please refresh and try again.');
      return;
    }
    
    const sprint: Sprint = {
      id: generateId('sprint'),
      organizationId: normalizedOrganizationId,
      projectId,
      name: formData.name,
      startDate: formData.startDate,
      endDate: formData.endDate,
      status: 'Planning',
      taskIds: selectedTaskIds,
      goals: formData.goals,
      createdAt: new Date().toISOString(),
    };

    await onCreate(sprint);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl">Create New Sprint</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted/40 rounded-lg"
            title="Close modal"
            aria-label="Close create sprint modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label htmlFor="create-sprint-name" className="block mb-2">Sprint Name *</label>
            <input
              id="create-sprint-name"
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="e.g., Sprint 1, Q1 2026 Sprint 2"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="create-sprint-start-date" className="block mb-2">Start Date *</label>
              <input
                id="create-sprint-start-date"
                type="date"
                required
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            <div>
              <label htmlFor="create-sprint-end-date" className="block mb-2">End Date *</label>
              <input
                id="create-sprint-end-date"
                type="date"
                required
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>

          <div>
            <label htmlFor="create-sprint-goals" className="block mb-2">Sprint Goals</label>
            <textarea
              id="create-sprint-goals"
              value={formData.goals}
              onChange={(e) => setFormData({ ...formData, goals: e.target.value })}
              className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
              rows={3}
              placeholder="What are the main objectives for this sprint?"
            />
          </div>

          {/* Task Selection */}
          <div>
            <label className="block mb-2">Add Tasks to Sprint</label>
            <p className="text-sm text-muted-foreground mb-3">
              Note: Tasks can be part of multiple sprints
            </p>
            {existingTasks.length === 0 ? (
              <p className="text-muted-foreground italic text-sm">No tasks available. Create tasks first.</p>
            ) : (
              <div className="border border-border rounded-lg p-3 max-h-64 overflow-y-auto space-y-2">
                {existingTasks.map((task) => (
                  <label key={task.id} className="flex items-start gap-3 p-2 hover:bg-muted/40 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedTaskIds.includes(task.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTaskIds([...selectedTaskIds, task.id]);
                        } else {
                          setSelectedTaskIds(selectedTaskIds.filter((id) => id !== task.id));
                        }
                      }}
                      className="w-4 h-4 mt-1"
                    />
                    <div className="flex-1">
                      <p className="text-sm">{task.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          task.type === 'Bug' ? 'bg-destructive/10 text-destructive' :
                          task.type === 'Feature' ? 'bg-primary/10 text-primary' :
                          'bg-chart-4/10 text-chart-4'
                        }`}>
                          {task.type}
                        </span>
                        <span className="text-xs text-muted-foreground">{task.status}</span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
            {selectedTaskIds.length > 0 && (
              <p className="text-sm text-muted-foreground mt-2">
                {selectedTaskIds.length} task{selectedTaskIds.length !== 1 ? 's' : ''} selected
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-border rounded-lg hover:bg-muted/40"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Create Sprint
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
